import NextLink from 'next/link'
import clsx from 'clsx'
import { useMemo } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'
import { Shimmer } from '@wartek-id/shimmer'

import { PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { assetUrl } from 'utils/url'
import {
  useAPIPelaksanaanProgress,
  useAPIGetPengembanganKompetensi,
  useAPIKinerja,
} from 'api/kinerja/useAPIKinerja'
import { isValidPerencanaanStatus } from 'app/KinerjaPamongPage/utils/perencanaan'
import { usePelaksanaanDate } from 'app/KinerjaShared/hooks/usePelaksanaanDate'
import useToggleSdkContext from 'utils/useToogleSdk'

export type PelaksanaanPegawaiCardProps = {
  className?: string
}

export function PelaksanaanPegawaiCard({
  className,
}: PelaksanaanPegawaiCardProps) {
  // -----------------------------------------
  // Resolve toggle
  // -----------------------------------------
  const toggles = useToggleSdkContext()
  const isPelaksanaanPegawaiEnabled =
    toggles?.['kinerja-spnf-pamong-pelaksanaan-enabled'] === true

  // -----------------------------------------
  // Resolve pelaksanaan date passed
  // -----------------------------------------
  const { isStarted: isPelaksanaanPhaseStarted } = usePelaksanaanDate()

  // -----------------------------------------
  // Get Perencanaan data
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        enabled: isPelaksanaanPegawaiEnabled,
        staleTime: 60 * 60 * 1000, // 1 hour
      },
      {
        tipe: 'SPNF',
      }
    )
  const isPerencanaanCompleted: boolean = useMemo(() => {
    const status = perencanaanData?.data?.status
    if (!isValidPerencanaanStatus(status)) false

    return status === 'READY'
  }, [perencanaanData])

  // -----------------------------------------
  // Get Pelaksanaan Progress Data
  // -----------------------------------------
  const {
    data: pelaksanaanProgressData,
    isLoading: isPelaksanaanProgressLoading,
  } = useAPIPelaksanaanProgress({
    tipe: 'SPNF',
    opts: {
      enabled: isPelaksanaanPegawaiEnabled,
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  // Get pengembangan kompetensi data
  const {
    data: pengembanganKompetensiData,
    isLoading: isPengembanganKompetensiLoading,
  } = useAPIGetPengembanganKompetensi({
    params: {
      tipe: 'SPNF',
    },
    options: {
      enabled: isPelaksanaanPegawaiEnabled,
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  // -----------------------------------------
  // Derived value
  // -----------------------------------------
  const submittedStatuses = useMemo(() => {
    return {
      tugasPokok: ['SUBMITTED', 'APPROVED'].includes(
        pelaksanaanProgressData?.data?.realisasiState?.primaryTask
      ),
      pengembanganKompetensi: ['SUBMITTED', 'APPROVED'].includes(
        pengembanganKompetensiData?.data?.state
      ),
      praktikKinerja: pelaksanaanProgressData?.data?.state === 'COMPLETED',
    }
  }, [pelaksanaanProgressData, pengembanganKompetensiData])

  const showCompletedPelaksanaanBanner = useMemo(() => {
    const data = pelaksanaanProgressData?.data
    if (!data) return false

    const isPengembanganKompetensiDone =
      data.realisasiState.competencyImprovement === 'COMPLETED'
    const isTugasPokokDone = data.realisasiState.primaryTask === 'APPROVED'
    const isPerilakuKerjaDone = data.penilaianState === 'SUBMITTED'
    const isPraktikKinerjaDone = data?.state === 'COMPLETED'
    const isPredikatSent = data.evaluationDocumentState === 'READY'

    return (
      isPraktikKinerjaDone &&
      isTugasPokokDone &&
      isPengembanganKompetensiDone &&
      isPerilakuKerjaDone &&
      !isPredikatSent
    )
  }, [pelaksanaanProgressData])

  const isLoading =
    isPelaksanaanProgressLoading ||
    isPengembanganKompetensiLoading ||
    isPerencanaanLoading

  // Hide the card if the toggle is off
  if (!isPelaksanaanPegawaiEnabled) {
    return null
  }

  if (isLoading) {
    return <Shimmer className="!w-full !h-96" />
  }

  if (!isPelaksanaanPhaseStarted || !isPerencanaanCompleted) {
    return null
  }

  return (
    <>
      {showCompletedPelaksanaanBanner && (
        <section className="bg-white shadow-card rounded-lg p-6 flex space-x-6 items-center flex-row">
          <div className="flex-shrink-0">
            <img
              alt=""
              src={assetUrl('/illustrations/img-thumbs-up.png')}
              width={84}
              height={84}
            />
          </div>

          <div className="space-y-1">
            <Text variant="heading-lg" as="h2">
              Pelaksanaan Kinerja sudah selesai!
            </Text>
            <Text color="subdued">
              Anda dapat konfirmasi ke Kepala SKB terkait{' '}
              <strong>Hasil Penilaian Kinerja</strong> Anda. Cek lagi halaman
              ini nanti, ya.
            </Text>
          </div>
        </section>
      )}
      <section
        className={clsx(
          'p-6 shadow-card rounded-lg bg-white space-y-6',
          className
        )}
      >
        <header className="flex items-center space-x-6">
          <img
            alt=""
            src={assetUrl('/illustrations/img-assessment-indicator.png')}
            width={84}
            height={84}
            className="flex-shrink-0"
          />

          <div className="space-y-1">
            <Text variant="heading-lg" as="h2">
              Pelaksanaan Kinerja
            </Text>
            <Text color="subdued">
              Laksanakan berdasarkan Perencanaan Anda.
            </Text>
          </div>
        </header>

        <div className="px-3 space-y-4">
          <div className="flex items-center justify-start gap-4 px-6 py-4 border rounded-lg border-gray-30">
            <img
              src={assetUrl('/illustrations/img-badge-star-ribbon-pro.png')}
              alt=""
              width={64}
              height={64}
              className="flex-shrink-0"
            />

            <Text variant="heading-sm" as="h3">
              Pelaksanaan Tugas Pokok
            </Text>

            <NextLink
              href={`${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/pelaksanaan/tugas-pokok`}
              passHref
            >
              <Button
                as="a"
                type={undefined}
                color={
                  submittedStatuses.tugasPokok === true ? 'white' : 'black'
                }
                className="ml-auto min-w-40"
              >
                {submittedStatuses.tugasPokok === true ? 'Cek' : 'Lakukan'}{' '}
                <span className="sr-only">Pelaksanaan Tugas Pokok</span>
              </Button>
            </NextLink>
          </div>

          <div className="flex items-center justify-start gap-4 px-6 py-4 border rounded-lg border-gray-30">
            <img
              src={assetUrl('/illustrations/img-badge-star.png')}
              alt=""
              width={64}
              height={64}
              className="flex-shrink-0"
            />

            <Text variant="heading-sm" as="h3">
              Praktik Kinerja
            </Text>

            <NextLink
              href={`${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`}
              passHref
            >
              <Button
                as="a"
                type={undefined}
                color={
                  submittedStatuses.praktikKinerja === true ? 'white' : 'black'
                }
                className="min-w-40 ml-auto !inline-flex"
              >
                {submittedStatuses.praktikKinerja === true ? 'Cek' : 'Lakukan'}{' '}
                <span className="sr-only">Praktik Kinerja</span>
              </Button>
            </NextLink>
          </div>

          <div className="flex items-center justify-start gap-4 px-6 py-4 border rounded-lg border-gray-30">
            <img
              src={assetUrl('/illustrations/img-document.png')}
              alt=""
              width={64}
              height={64}
              className="flex-shrink-0"
            />

            <Text variant="heading-sm" as="h3">
              Pengembangan Kompetensi
            </Text>

            <NextLink
              href={`${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/pelaksanaan/pengembangan-kompetensi`}
              passHref
            >
              <Button
                as="a"
                type={undefined}
                color={
                  submittedStatuses.pengembanganKompetensi === true
                    ? 'white'
                    : 'black'
                }
                className="ml-auto min-w-40"
              >
                {submittedStatuses.pengembanganKompetensi === true
                  ? 'Cek'
                  : 'Lakukan'}
                <span className="sr-only">Pengembangan Kompetensi</span>
              </Button>
            </NextLink>
          </div>

          <div className="flex items-center justify-start gap-4 px-6 py-4 border rounded-lg border-gray-30">
            <Text variant="heading-sm" as="h3">
              Perilaku Kerja
            </Text>

            <NextLink
              href={`${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/pelaksanaan/perilaku-kerja`}
              passHref
            >
              <Button
                className="ml-auto min-w-40"
                as="a"
                type={undefined}
                color="white"
              >
                Cek <span className="sr-only">Perilaku Kerja</span>
              </Button>
            </NextLink>
          </div>
        </div>
      </section>
    </>
  )
}
